import { IconButton, Stack } from '@mui/material';
import { InvoiceOverview } from '@vy/api-definition';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { INVOICE_PDF_TYPES } from '@vy/types';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { withDefault } from '@vy/utils';
import { useLightInvoices } from '../../../../hooks/requests/use-get-light-invoices';
import { useConnectedIntegrations } from '../../../../hooks/requests/use-integrations';
import { useUsageEventsReportConfiguration } from '../../../../hooks/requests/use-usage-events-report-configuration';
import { useDialog } from '../../../../hooks/use-dialog';
import { useValidatedParams } from '../../../../hooks/use-validated-params';
import { useInvoiceActions } from '../../hooks/use-invoice-actions';
import { ApplyCreditDialog } from '../../invoices-components/apply-credit-dialog/ApplyCreditDialog';
import { InvoicesActionsMenu } from '../../InvoicesPage/invoices-page-components/invoices-actions-menu/InvoicesActionsMenu';
import { MarkAsPaidDialog } from '../../InvoicesPage/invoices-page-components/MarkAsPaidDialog/MarkAsPaidDialog';
import { SyncInvoicesAsSalesOrderDialog } from '../../InvoicesPage/invoices-page-components/SyncInvoicesAsSalesOrderDialog/SyncInvoicesAsSalesOrderDialog';
import { InvoiceActivity } from './InvoiceActivity/InvoiceActivity';

export const CustomerInvoicePageTabsAction: React.FC = () => {
  const navigate = useNavigate();
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const {
    isDialogOpen: isApplyCreditDialogOpen,
    handleOpen: handleOpenApplyCredit,
    handleClose: handleCloseApplyCredit,
  } = useDialog();
  const { invoiceId } = useValidatedParams<['invoiceId']>('invoiceId');

  const { data: invoicesData } = useLightInvoices([invoiceId], { suspense: true });
  const { data: connectedIntegrations = [] } = useConnectedIntegrations();
  const { data: reportConfig } = useUsageEventsReportConfiguration();
  const hasUsageEventsReportConfiguration = reportConfig?.configured ?? false;

  const [invoice] = withDefault(invoicesData?.invoices, []);

  const {
    approveSelectedInvoice,
    downloadInvoicePdf,
    downloadUsageEventsReport,
    syncToNetSuite,
    syncToNetSuiteSalesOrder,
    confirmSyncToNetSuiteSalesOrder,
    isSalesOrderConfirmDialogOpen,
    handleCloseSalesOrderConfirmDialog,
    syncToStripe,
    refund,
    grantCredit,
    isInvoiceApplicableForSync,
    handleSyncToQuickBooks,
    handleSyncToMorning,
    handleSyncToMarketplace,
    handleSyncToXero,
    handleSyncToPaddle,
  } = useInvoiceActions(invoice as unknown as InvoiceOverview);

  return (
    <Stack direction="row" sx={{
      alignItems: "center"
    }}>
      <InvoiceActivity invoice={invoice} systemEvents={invoicesData?.invoicesSystemEvents ?? []} />
      <InvoicesActionsMenu
        invoices={[invoice as unknown as InvoiceOverview]}
        contractName={invoice.contract?.contractName ?? ''}
        onDownloadAsPdf={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Light)}
        onDownloadAsPdfStatement={() => downloadInvoicePdf(INVOICE_PDF_TYPES.Statement)}
        onDownloadUsageEventsReport={downloadUsageEventsReport}
        hasUsageEventsReportConfiguration={hasUsageEventsReportConfiguration}
        onSyncToNetSuite={syncToNetSuite}
        onSyncToNetSuiteSalesOrder={syncToNetSuiteSalesOrder}
        onSyncToStripe={syncToStripe}
        onApproveInvoices={approveSelectedInvoice}
        connectedIntegrations={connectedIntegrations}
        syncToNetSuiteDisabled={!isInvoiceApplicableForSync}
        onMarkAsPaid={handleOpen}
        onSuccessfulRemoval={() => navigate(`/customers/${invoice.customer.id}/invoices`)}
        onRefund={refund}
        onSyncToQuickBooks={handleSyncToQuickBooks}
        onSyncToMorning={handleSyncToMorning}
        onSyncToMarketplace={handleSyncToMarketplace}
        onSyncToXero={handleSyncToXero}
        onSyncToPaddle={handleSyncToPaddle}
        disableAddToInvoice={false}
        onGrantCredits={() =>
          grantCredit({
            invoiceId: invoice.id,
            contractId: invoice.contract?.contractId,
            customerId: invoice.customer.id,
          })
        }
        onApplyCredit={handleOpenApplyCredit}
        disableRefund={false}
        disableCreditGrant={false}
      >
        <IconButton {...getSelectorAttribute(invoicesOverviewPageSelectors.dotsMenu.dotOptions)}>
          <VyIcon icon="DotsMenu" color="#323232" />
        </IconButton>
      </InvoicesActionsMenu>
      {isDialogOpen && (
        <MarkAsPaidDialog
          open={isDialogOpen}
          invoiceId={invoice.id}
          onClose={handleClose}
          customerName={invoice.customer.name}
          customerId={invoice.customer.id}
          currency={invoice.currency}
        />
      )}
      <SyncInvoicesAsSalesOrderDialog
        open={isSalesOrderConfirmDialogOpen}
        onClose={handleCloseSalesOrderConfirmDialog}
        invoices={[invoice as unknown as InvoiceOverview]}
        onSyncInvoices={confirmSyncToNetSuiteSalesOrder}
      />
      {isApplyCreditDialogOpen && (
        <ApplyCreditDialog
          open={isApplyCreditDialogOpen}
          onClose={handleCloseApplyCredit}
          invoiceId={invoice.id}
          invoiceName={invoice.name}
          customerId={invoice.customer.id}
          contractId={invoice.contract?.contractId}
          maxAmount={invoice.revenueBreakdown?.total ?? 0}
          currency={invoice.currency}
        />
      )}
    </Stack>
  );
};
